/**
 * GuildPluginSharingCard — the owner's control for whether PLUGIN arches may
 * create shared workspaces for this guild (Settings tab of Guild Management).
 *
 * Deliberately a separate control from GuildDataSpaceCard, not a second line
 * in it. That card governs the guild's Vault space: platform-curated document
 * and note storage. This one exposes the guild to THIRD-PARTY plugin code —
 * it is the only channel through which a plugin can learn the guild exists,
 * and it lets a plugin claim a shared data space inside its own storage. An
 * owner who wanted shared documents has not thereby agreed to that, so it
 * takes its own decision and its own confirmation.
 *
 * Unlike the Vault space this IS reversible: plugin data lives in each
 * arche's own store rather than in member-visible platform surfaces, so
 * turning it off strands nothing — it makes the guild ineligible immediately
 * and the data becomes unreachable until it is turned back on.
 *
 * The same switch is the owner's consent for the guild CHAT reaching an app:
 * when a Fias admin links a public guild to an app's game config, that app may
 * show members the guild's main channel, let people join the guild from
 * inside it, and let members send messages there as themselves (LitCraft
 * Player `get_guild_chat` / `join_guild_chat` / `post_guild_chat` /
 * `subscribe_guild_chat`). The copy
 * below says so in plain words, because this is the one decision that allows
 * it — see docs/guild-plugin-data-sharing.md §4.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/use-toast';
import { Blocks } from 'lucide-react';
import { api, type GuildDetailResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function GuildPluginSharingCard({ guild }: { guild: GuildDetailResponse }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const isOwner = guild.myRole === 'owner';
  const enabled = guild.pluginSharingEnabled === true;

  const setSharing = useMutation({
    mutationFn: (next: boolean) => api.setGuildPluginSharing(guild.guildId, next),
    onSuccess: async (_res, next) => {
      await qc.invalidateQueries({ queryKey: queryKeys.guilds.detail(guild.guildId) });
      setConfirming(false);
      toast({
        title: next ? 'Plugin sharing enabled' : 'Plugin sharing turned off',
        description: next
          ? 'Members can now share plugin data with this guild.'
          : 'Existing shared plugin spaces are no longer reachable, and linked apps stop showing this guild’s chat. Turning this back on restores both.',
      });
    },
    onError: (err) => {
      toast({
        title: 'Could not change plugin sharing',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="text-base flex items-center gap-2">
            <Blocks className="h-4 w-4" aria-hidden="true" />
            Plugin data sharing
          </CardTitle>
          <CardDescription>
            Let apps your members use keep shared data for this guild, and let an app Fias links to
            this guild show its chat.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {enabled ? (
          <>
            <p className="text-sm text-muted-foreground">
              Enabled — members can share an app&rsquo;s data with this guild. Every member can view
              it; moderators and admins (and custom roles granted &ldquo;Manage shared data&rdquo;)
              can add and edit. Each app keeps its own separate space. An app Fias has linked to
              this guild can also show members the guild&rsquo;s main chat, let people join the
              guild from inside the app, and let members send messages there as themselves.
            </p>
            {isOwner && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={setSharing.isPending}
                onClick={() => setSharing.mutate(false)}
              >
                {setSharing.isPending ? 'Turning off…' : 'Turn off plugin sharing'}
              </Button>
            )}
          </>
        ) : isOwner ? (
          <>
            <p className="text-sm text-muted-foreground">
              Not enabled. Turning this on lets apps your members use see this guild&rsquo;s name
              and create a shared space for it
              {guild.visibility === 'public'
                ? ' — and anyone who joins this public guild gets access to what is in it'
                : ''}
              . It also lets an app Fias links to this guild show members its main chat, let people
              join the guild from inside the app, and let members send messages there as themselves.
              You can turn it off again at any time.
            </p>
            <Button type="button" size="sm" onClick={() => setConfirming(true)}>
              Enable plugin sharing
            </Button>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            Not enabled. Only the guild owner can turn on plugin data sharing.
          </p>
        )}
      </CardContent>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Allow apps to share data with “{guild.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Apps your members use — including ones built by other people — will be able to see
              that this guild exists and offer it as a place to share their data. A member can then
              create a shared space that every member can read, and that moderators and admins can
              write to.
              {guild.visibility === 'public' && (
                <> This guild is public: anyone who joins can read what is in those spaces.</>
              )}{' '}
              Each app&rsquo;s space is separate and stays inside that app.
              <br />
              <br />
              <span data-testid="plugin-sharing-chat-consent">
                Guild chat: if Fias links this guild to an app (only possible while the guild is
                public), that app can show your members the messages in the guild&rsquo;s main chat
                channel, let people join this guild — and so its chat — from inside the app, and let
                members send messages there as themselves. It never sees direct messages or any
                other channel.
              </span>{' '}
              You can turn all of this off again at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={setSharing.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={setSharing.isPending}
              onClick={(e) => {
                e.preventDefault();
                setSharing.mutate(true);
              }}
            >
              {setSharing.isPending ? 'Enabling…' : 'Enable plugin sharing'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
